import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useIsAdmin } from '../../hooks/useIsAdmin';
import { useTheme } from '../../contexts/ThemeContext';
import { currentLang } from '../../i18n';
import { trackEvent } from '../../services/firstPartyAnalytics';
import { internalPathFromHref } from '../common/LinkifiedText';
import PopupView from './PopupView';
import { PopupState, PublicPopup, nextState, normalizeScreenPath, pickPopupForScreen } from '../../utils/popupRules';

/**
 * Screen-Popups, Live-Anzeige im Web (22.10.2026, Phase 4): holt die aktiven Popups einmal je Sitzung
 * (Cache 10 min, neu nach Login/Logout), entscheidet bei jedem Routenwechsel mit den PURE-Regeln, zählt lokal
 * (localStorage) + Server + Analytics. Höchstens ein Popup je Seitenaufruf, nie auf Admin-Seiten.
 */
export const STORAGE_KEY = 'dc-screen-popups-v1';
export const CACHE_MS = 10 * 60 * 1000;

const obj = (v: unknown): Record<string, unknown> => (v != null && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {});

export const readStates = (): Record<string, PopupState> => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, PopupState>) : {};
  } catch { return {}; }
};
export const writeStates = (states: Record<string, PopupState>): void => { try { localStorage.setItem(STORAGE_KEY, JSON.stringify(states)); } catch { /* privat/blockiert */ } };

/** Serverzustand hat Vorrang, wenn er mehr kennt oder einen neuen versionKey trägt. PURE. */
export const mergeStates = (local: Record<string, PopupState>, server: Record<string, PopupState>): Record<string, PopupState> => {
  const out = { ...local };
  for (const [id, s] of Object.entries(server)) {
    const l = out[id];
    if (!l || s.versionKey !== l.versionKey || (s.shows ?? 0) >= (l.shows ?? 0)) out[id] = s;
  }
  return out;
};

export const parsePublicPopups = (data: unknown): { popups: PublicPopup[]; states: Record<string, PopupState> } => {
  const o = obj(data);
  const popups = (Array.isArray(o.popups) ? o.popups : []).filter((p): p is PublicPopup => !!p && typeof p === 'object' && typeof (p as PublicPopup).id === 'string');
  const states: Record<string, PopupState> = {};
  for (const [k, v] of Object.entries(obj(o.states))) { const s = obj(v); states[k] = { shows: Number(s.shows) || 0, lastShownAt: (s.lastShownAt as number) ?? null, dismissedAt: (s.dismissedAt as number) ?? null, versionKey: (s.versionKey as string) ?? null }; }
  return { popups, states };
};

const ScreenPopupHost: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const isAdmin = useIsAdmin();
  const { theme } = useTheme();
  const [active, setActive] = useState<{ popup: PublicPopup; screen: string } | null>(null);
  const popupsRef = useRef<PublicPopup[]>([]);
  const statesRef = useRef<Record<string, PopupState>>(readStates());
  const sessionShown = useRef<Set<string>>(new Set());
  const loadedAt = useRef<number>(0);
  const loadedFor = useRef<string | null>(null);
  const timer = useRef<number | null>(null);
  const screenRef = useRef<string>('');

  const ensureLoaded = useCallback(async (): Promise<boolean> => {
    const key = token ?? '';
    if (loadedAt.current && loadedFor.current === key && Date.now() - loadedAt.current < CACHE_MS) return true;
    try {
      const res = await api.get('/api/screen-popups', { params: { platform: 'web' } });
      const { popups, states } = parsePublicPopups(res.data);
      popupsRef.current = popups;
      statesRef.current = mergeStates(statesRef.current, states);
      writeStates(statesRef.current);
      loadedAt.current = Date.now();
      loadedFor.current = key;
      return true;
    } catch { return popupsRef.current.length > 0; }
  }, [token]);

  const record = useCallback(async (popup: PublicPopup, event: 'impression' | 'click' | 'dismiss', screen: string, button?: 'primary' | 'secondary') => {
    statesRef.current = { ...statesRef.current, [popup.id]: nextState(statesRef.current[popup.id] ?? null, popup, event, Date.now()) };
    writeStates(statesRef.current);
    Promise.resolve(trackEvent(`popup_${event}`, { popup_id: popup.id, screen, layout: popup.layout.kind, frequency: popup.frequency.mode, ...(button ? { button } : {}) }, user?.id ?? null)).catch(() => undefined);
    try {
      const res = await api.post(`/api/screen-popups/${popup.id}/event`, { event, screen, platform: 'web', ...(button ? { button } : {}) });
      const s = obj(obj(res.data).state);
      if (s && typeof s.shows === 'number') { statesRef.current = mergeStates(statesRef.current, { [popup.id]: { shows: s.shows as number, lastShownAt: (s.lastShownAt as number) ?? null, dismissedAt: (s.dismissedAt as number) ?? null, versionKey: (s.versionKey as string) ?? null } }); writeStates(statesRef.current); }
    } catch { /* fail-soft */ }
  }, [user?.id]);

  useEffect(() => {
    const screen = normalizeScreenPath(location.pathname);
    screenRef.current = screen;
    if (timer.current) { window.clearTimeout(timer.current); timer.current = null; }
    if (screen.startsWith('/admin') || active) return undefined;
    let cancelled = false;
    (async () => {
      const ok = await ensureLoaded();
      if (!ok || cancelled || screenRef.current !== screen) return;
      const popup = pickPopupForScreen(popupsRef.current, screen, { platform: 'web', user: user ? { id: String(user.id ?? '') } : null, isAdmin, statesById: statesRef.current, sessionShown: sessionShown.current });
      if (!popup) return;
      timer.current = window.setTimeout(() => {
        if (screenRef.current !== screen) return;
        sessionShown.current.add(popup.id);
        record(popup, 'impression', screen);
        setActive({ popup, screen });
      }, Math.max(0, popup.trigger.delayMs));
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, token]);

  if (!active) return null;
  const close = (dismiss = true) => { if (dismiss) record(active.popup, 'dismiss', active.screen); setActive(null); };
  const onButton = (which: 'primary' | 'secondary') => {
    const b = which === 'primary' ? active.popup.content.primaryButton : active.popup.content.secondaryButton;
    record(active.popup, 'click', active.screen, which);
    setActive(null);
    const target = (b?.target ?? '').trim();
    if (b?.action === 'route' && target) navigate(internalPathFromHref(target) ?? target);
    else if (b?.action === 'url' && target) window.open(target, '_blank', 'noopener');
  };
  return <PopupView popup={active.popup} lang={currentLang()} isLight={theme === 'light'} onClose={() => close(true)} onButton={onButton} />;
};

export default ScreenPopupHost;
